import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Copy, Square, FileCheck2 } from "lucide-react";
import { gapi, backendUrl, errMsg, fmtDateTime } from "../api";
import { useGAuth } from "../GAuth";
import { PageHeader, Panel, Btn, inputCls } from "../components/ui";
import { Field, ContactPicker } from "../components/common";

async function streamDraft(body, onDelta, signal) {
  const res = await fetch(backendUrl("/api/g/ai/draft"), { method: "POST", credentials: "include", signal, headers: { "Content-Type": "application/json", "X-CG-Request": "1" }, body: JSON.stringify(body) });
  if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(typeof j.detail === "string" ? j.detail : "Generazione non riuscita"); }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      const ev = JSON.parse(p.slice(6));
      if (ev.error) throw new Error(ev.error);
      if (ev.delta) onDelta(ev.delta);
    }
  }
}

export default function AssistenteAI() {
  const { can } = useGAuth();
  const [params] = useSearchParams();
  const [meta, setMeta] = useState({ tipi: {}, toni: {} });
  const [props, setProps] = useState([]);
  const [f, setF] = useState({ tipo: params.get("propertyId") ? "descrizione_immobile" : "email_cliente", tono: "professionale", lunghezza: "media", propertyId: params.get("propertyId") || "", contactId: "", istruzioni: "" });
  const [contact, setContact] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState([]);
  const ctrl = useRef(null);
  const loadHist = () => gapi.get("/ai/drafts").then((r) => setHist(r.data)).catch(() => {});
  useEffect(() => {
    gapi.get("/ai/meta").then((r) => setMeta(r.data)); loadHist();
    if (can("immobili")) gapi.get("/properties", { params: { size: 100 } }).then((r) => setProps(r.data.items)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const run = async () => {
    setText(""); setBusy(true); ctrl.current = new AbortController();
    try { await streamDraft({ ...f, contactId: contact?.id || "" }, (d) => setText((t) => t + d), ctrl.current.signal); loadHist(); } catch (e) { if (e.name !== "AbortError") toast.error(e.message); } finally { setBusy(false); }
  };
  const applyToProperty = async () => {
    try {
      const { data: p } = await gapi.get(`/properties/${f.propertyId}`);
      await gapi.put(`/properties/${f.propertyId}`, { descrizione: text, version: p.version });
      toast.success("Descrizione salvata nella scheda immobile");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const needsProp = ["descrizione_immobile", "annuncio_breve", "email_proprietario"].includes(f.tipo);
  return (
    <div data-testid="g-ai-page">
      <PageHeader eyebrow="Strumenti" title="Assistente AI" desc="Bozze di descrizioni, annunci ed email generate dai dati del gestionale. Rileggi sempre il testo prima di usarlo: i dati mancanti sono segnati tra [parentesi]." />
      <div className="grid lg:grid-cols-[380px_1fr] gap-4 items-start">
        <Panel title="Cosa vuoi scrivere?" testid="ai-form">
          <div className="space-y-3">
            <Field label="Tipo di testo"><select className={inputCls} value={f.tipo} onChange={set("tipo")} data-testid="ai-type">{Object.entries(meta.tipi).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
            {props.length > 0 && <Field label={needsProp ? "Immobile" : "Immobile (facoltativo)"}><select className={inputCls} value={f.propertyId} onChange={set("propertyId")} data-testid="ai-property"><option value="">—</option>{props.map((p) => <option key={p.id} value={p.id}>{`${p.codiceRif} · ${p.titolo}`}</option>)}</select></Field>}
            {f.tipo.startsWith("email") && can("contatti") && <Field label="Destinatario (facoltativo)"><ContactPicker value={contact} onChange={setContact} testid="ai-contact" /></Field>}
            <div className="grid grid-cols-2 gap-2">
              <Field label="Tono"><select className={inputCls} value={f.tono} onChange={set("tono")} data-testid="ai-tone">{Object.entries(meta.toni).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
              <Field label="Lunghezza"><select className={inputCls} value={f.lunghezza} onChange={set("lunghezza")} data-testid="ai-length"><option value="breve">Breve</option><option value="media">Media</option><option value="lunga">Lunga</option></select></Field>
            </div>
            <Field label="Indicazioni (facoltative)"><textarea className={`${inputCls} h-24 py-2`} placeholder="Es. invita a una visita sabato mattina, sottolinea il terrazzo" value={f.istruzioni} onChange={set("istruzioni")} data-testid="ai-instructions" /></Field>
            {busy ? <Btn variant="ghost" onClick={() => ctrl.current?.abort()} data-testid="ai-stop"><Square className="h-4 w-4" /> Interrompi</Btn>
              : <Btn onClick={run} disabled={needsProp && !f.propertyId} data-testid="ai-generate"><Sparkles className="h-4 w-4" /> Genera bozza</Btn>}
            <p className="text-xs text-anthracite/45">All'AI vengono inviati solo i dati pubblicabili dell'immobile e il nome del destinatario: mai telefoni, email, prezzo minimo o note riservate.</p>
          </div>
        </Panel>
        <div className="space-y-4">
          <Panel title="Bozza" testid="ai-output-panel" actions={text && !busy && <div className="flex gap-2">
            <Btn variant="ghost" className="h-9" onClick={() => navigator.clipboard.writeText(text).then(() => toast.success("Copiato"))} data-testid="ai-copy"><Copy className="h-4 w-4" /> Copia</Btn>
            {f.tipo === "descrizione_immobile" && f.propertyId && can("immobili", "modificare") && <Btn className="h-9" onClick={applyToProperty} data-testid="ai-apply"><FileCheck2 className="h-4 w-4" /> Usa come descrizione</Btn>}
          </div>}>
            <textarea className="w-full min-h-[320px] rounded-xl border border-border-subtle bg-warm/40 p-4 text-sm leading-relaxed outline-none focus:border-brand" value={text} onChange={(e) => setText(e.target.value)}
              placeholder={busy ? "Sto scrivendo…" : "La bozza apparirà qui, e potrai modificarla prima di copiarla."} data-testid="ai-output" />
          </Panel>
          <Panel title="Bozze recenti" testid="ai-history">
            <ul className="divide-y divide-border-subtle text-sm">{hist.map((h) => (
              <li key={h.id} className="py-2 cursor-pointer hover:bg-accent-subtle/40 rounded px-2" onClick={() => setText(h.testo)} data-testid="ai-history-row">
                <b>{meta.tipi[h.tipo] || h.tipo}</b> <span className="text-xs text-anthracite/50">· {fmtDateTime(h.createdAt)}</span><p className="text-xs text-anthracite/60 line-clamp-2">{h.testo}</p></li>))}
              {!hist.length && <li className="py-2 text-anthracite/45">Nessuna bozza ancora.</li>}</ul>
          </Panel>
        </div>
      </div>
    </div>
  );
}
